import {
  AGENT_LIMITS,
  FINAL_ANSWER_TOOL,
  type TAgentOutput,
  type TAgentOutputField,
  type TAgentTool,
  type TAgentToolField,
  type THookpipeAction,
} from './agent-config.schema';

export type TJsonSchemaObject = Record<string, unknown>;

export interface IAgentToolSpec {
  name: string;
  description: string;
  inputSchema: TJsonSchemaObject;
}

type TParam = Pick<TAgentToolField, 'path' | 'type' | 'required' | 'description'> &
  Partial<Pick<TAgentToolField, 'enum' | 'min' | 'max'>>;

const param = (
  path: string,
  type: TAgentToolField['type'],
  required: boolean,
  description: string,
  extra: Partial<TParam> = {},
): TParam => ({ path, type, required, description, ...extra });

// What each Hookpipe action asks of the agent (§7.5); a step may fix any of them instead.
export const HOOKPIPE_ACTION_PARAMS: Record<THookpipeAction, TParam[]> = {
  search_issues: [
    param('flow_id', 'string', false, 'Only issues of this flow (flw_…).'),
    param('state', 'string', false, 'open (default) or resolved.', { enum: ['open', 'resolved'] }),
    param('limit', 'integer', false, 'At most this many, up to 20.', { min: 1, max: 20 }),
  ],
  read_issue: [param('issue_id', 'string', true, 'The issue id (iss_…).')],
  search_runs: [
    param('flow_id', 'string', true, 'The flow whose runs to list (flw_…).'),
    param('status', 'string', false, 'Only runs with this status: running, completed, failed…'),
    param('limit', 'integer', false, 'At most this many, up to 20.', { min: 1, max: 20 }),
  ],
  read_run: [param('run_id', 'string', true, 'The run id.')],
  redeliver_batch: [
    param('flow_id', 'string', true, 'The flow whose failed copies to redeliver (flw_…).'),
    param('step', 'string', true, 'The key of the step that failed, like n_deliver.'),
    param('since', 'string', true, 'From when: an ISO time, or a duration back from now like 2h.'),
  ],
  mute_issue: [
    param('issue_id', 'string', true, 'The issue to mute (iss_…).'),
    param('hours', 'integer', true, 'For how long: 1, 4 or 24 hours.', { min: 1, max: 24 }),
  ],
};

const HOOKPIPE_CHANGES_DATA: Record<THookpipeAction, boolean> = {
  search_issues: false,
  read_issue: false,
  search_runs: false,
  read_run: false,
  redeliver_batch: true,
  mute_issue: true,
};

export const hookpipeChangesData = (action: THookpipeAction): boolean =>
  HOOKPIPE_CHANGES_DATA[action];

/** The fields a call carries: a Hookpipe action's own set, overridden by what the step fixed. */
export function effectiveFields(tool: TAgentTool): TAgentToolField[] {
  if (tool.kind !== 'hookpipe') return tool.fields;
  const configured = new Map(tool.fields.map((field) => [field.path, field]));
  return HOOKPIPE_ACTION_PARAMS[tool.config.action].map((spec) => {
    const own = configured.get(spec.path);
    if (own?.fill === 'fixed') return { ...own, type: spec.type };
    return { fill: 'agent' as const, ...spec, description: own?.description || spec.description };
  });
}

/** Decided on the input, before the call (§7.1): Code never changes data; GET/HEAD read. */
export function toolChangesData(tool: TAgentTool): boolean {
  if (tool.kind === 'code') return false;
  if (tool.changesData !== undefined) return tool.changesData;
  if (tool.kind === 'hookpipe') return HOOKPIPE_CHANGES_DATA[tool.config.action];
  if (tool.kind === 'run_flow') return true;
  if (tool.config.connector === 'http') {
    const method = String(tool.config.input.method ?? 'GET').toUpperCase();
    return !['GET', 'HEAD'].includes(method);
  }
  return true;
}

function fieldSchema(field: {
  type: string;
  description?: string;
  enum?: string[];
  min?: number;
  max?: number;
}): TJsonSchemaObject {
  const type = field.type === 'list' ? 'array' : field.type;
  const schema: TJsonSchemaObject = { type };
  if (field.description) schema.description = field.description;
  if (field.enum && field.enum.length > 0) schema.enum = field.enum;
  if (type === 'number' || type === 'integer') {
    if (field.min !== undefined) schema.minimum = field.min;
    if (field.max !== undefined) schema.maximum = field.max;
  }
  if (type === 'array') schema.items = {};
  if (type === 'object') schema.additionalProperties = true;
  return schema;
}

/** What the model sees of a tool: its name, words, and the JSON Schema of its agent fields. */
export function describeTool(tool: TAgentTool): IAgentToolSpec {
  const agent = effectiveFields(tool).filter((field) => field.fill === 'agent');
  const properties: Record<string, TJsonSchemaObject> = {};
  for (const field of agent) properties[field.path] = fieldSchema(field);
  return {
    name: tool.name,
    description: tool.description,
    inputSchema: {
      type: 'object',
      properties,
      required: agent.filter((field) => field.required).map((field) => field.path),
      additionalProperties: false,
    },
  };
}

export type TCheck<T> = { ok: true; value: T } | { ok: false; errors: string[] };

function typeProblem(name: string, type: string, value: unknown): string | null {
  const actual = Array.isArray(value) ? 'array' : value === null ? 'null' : typeof value;
  switch (type) {
    case 'string':
      return typeof value === 'string' ? null : `${name} must be text, not ${actual}`;
    case 'number':
      return typeof value === 'number' && Number.isFinite(value)
        ? null
        : `${name} must be a number, not ${actual}`;
    case 'integer':
      return Number.isInteger(value) ? null : `${name} must be a whole number, not ${actual}`;
    case 'boolean':
      return typeof value === 'boolean' ? null : `${name} must be true or false, not ${actual}`;
    case 'array':
    case 'list':
      return Array.isArray(value) ? null : `${name} must be a list, not ${actual}`;
    case 'object':
      return actual === 'object' ? null : `${name} must be an object, not ${actual}`;
    default:
      return null;
  }
}

function checkFields(
  specs: readonly {
    name: string;
    type: string;
    required: boolean;
    enum?: string[];
    min?: number;
    max?: number;
  }[],
  input: unknown,
  closed: boolean,
): TCheck<Record<string, unknown>> {
  if (input === null || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, errors: ['the input must be an object of named fields'] };
  }
  const given = input as Record<string, unknown>;
  const errors: string[] = [];
  const known = new Set(specs.map((spec) => spec.name));
  for (const spec of specs) {
    const value = given[spec.name];
    if (value === undefined || value === null) {
      if (spec.required) errors.push(`${spec.name} is required`);
      continue;
    }
    const problem = typeProblem(spec.name, spec.type, value);
    if (problem) {
      errors.push(problem);
      continue;
    }
    if (spec.enum && spec.enum.length > 0 && !spec.enum.includes(String(value))) {
      errors.push(`${spec.name} must be one of ${spec.enum.join(', ')}`);
    }
    if (typeof value === 'number') {
      if (spec.min !== undefined && value < spec.min)
        errors.push(`${spec.name} is below ${spec.min}`);
      if (spec.max !== undefined && value > spec.max)
        errors.push(`${spec.name} is above ${spec.max}`);
    }
  }
  if (closed) {
    for (const key of Object.keys(given)) {
      if (!known.has(key)) errors.push(`there is no field ${key}`);
    }
  }
  return errors.length > 0 ? { ok: false, errors } : { ok: true, value: given };
}

/** The model's input against the tool's agent fields; a mismatch goes back to it, unrun (§7.1). */
export function checkToolInput(tool: TAgentTool, input: unknown): TCheck<Record<string, unknown>> {
  const specs = effectiveFields(tool)
    .filter((field) => field.fill === 'agent')
    .map((field) => ({ ...field, name: field.path }));
  return checkFields(specs, input, true);
}

const outputSpecs = (fields: readonly TAgentOutputField[]) =>
  fields.map((field) => ({ ...field, type: field.type === 'list' ? 'array' : field.type }));

/** `final_answer` (decision 8): JSON fields or one `category`, the same way on every provider. */
export function finalAnswerTool(output: TAgentOutput): IAgentToolSpec | null {
  if (output.kind === 'text') return null;
  if (output.kind === 'categories') {
    return {
      name: FINAL_ANSWER_TOOL,
      description:
        'Give your answer: the one category that fits. Call it alone, after any other tools have finished.',
      inputSchema: {
        type: 'object',
        properties: {
          category: {
            type: 'string',
            enum: output.categories.map((category) => category.name),
            description: output.categories
              .map((category) =>
                category.description ? `${category.name}: ${category.description}` : category.name,
              )
              .join('\n'),
          },
        },
        required: ['category'],
        additionalProperties: false,
      },
    };
  }
  const properties: Record<string, TJsonSchemaObject> = {};
  for (const field of outputSpecs(output.fields)) properties[field.name] = fieldSchema(field);
  return {
    name: FINAL_ANSWER_TOOL,
    description:
      'Give your answer as these fields. Call it alone, after any other tools have finished.',
    inputSchema: {
      type: 'object',
      properties,
      required: output.fields.filter((field) => field.required).map((field) => field.name),
      additionalProperties: false,
    },
  };
}

export function checkAnswer(output: TAgentOutput, input: unknown): TCheck<unknown> {
  if (output.kind === 'text') return { ok: true, value: input };
  if (output.kind === 'categories') {
    return checkFields(
      [
        {
          name: 'category',
          type: 'string',
          required: true,
          enum: output.categories.map((category) => category.name),
        },
      ],
      input,
      true,
    );
  }
  return checkFields(outputSpecs(output.fields), input, true);
}

/**
 * §5.8: the model reads at most `maxChars` of a result — JSON cut by structure (first items,
 * "… N more"), text from the start — and always the note of how much it did not see.
 */
export function resultForModel(
  result: unknown,
  maxChars: number,
): { text: string; truncated: boolean } {
  const full = typeof result === 'string' ? result : JSON.stringify(result ?? null);
  if (full.length <= maxChars) return { text: full, truncated: false };
  const note = `\n[truncated: showing ${kb(maxChars)} of ${kb(full.length)}]`;
  const room = Math.max(100, maxChars - note.length);
  let shown = full.slice(0, room);
  if (typeof result !== 'string' && result !== null && typeof result === 'object') {
    for (const keep of [50, 20, 10, 5, 3, 1]) {
      const cut = JSON.stringify(shrink(result, keep, 0));
      if (cut.length <= room) {
        shown = cut;
        break;
      }
    }
  }
  return { text: `${shown}${note}`, truncated: true };
}

function shrink(value: unknown, keep: number, depth: number): unknown {
  if (depth > 12) return '…';
  if (Array.isArray(value)) {
    const head = value.slice(0, keep).map((item) => shrink(item, keep, depth + 1));
    return value.length > keep ? [...head, `… ${value.length - keep} more`] : head;
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        shrink(v, keep, depth + 1),
      ]),
    );
  }
  if (typeof value === 'string' && value.length > 2000) return `${value.slice(0, 2000)}…`;
  return value;
}

function kb(chars: number): string {
  return `${(chars / 1000).toFixed(1)} kB`;
}

export const AGENT_MAX_PARALLEL = AGENT_LIMITS.maxParallelTools;
